import { useCallback, useEffect, useRef, useState } from 'react';

import { ANONYMOUS } from '@tosspayments/tosspayments-sdk';

import { createPaymentWidgets } from './index';
import { TossPaymentRequestParams } from './types';

interface UseTossPaymentWidgetParams {
  customerKey: string | typeof ANONYMOUS;
  amount: number;
}

type WidgetsInstance = Awaited<ReturnType<typeof createPaymentWidgets>>;
type PaymentMethodWidget = Awaited<ReturnType<WidgetsInstance['renderPaymentMethods']>>;

const INIT_TIMEOUT_MS = 5000;

export function useTossPaymentWidget({
  customerKey,
  amount,
}: UseTossPaymentWidgetParams) {
  const widgetsRef = useRef<WidgetsInstance | null>(null);
  const paymentMethodWidgetRef = useRef<PaymentMethodWidget | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [initError, setInitError] = useState<Error | null>(null);

  // SDK 초기화 → widgets 인스턴스 생성 → 금액 설정
  useEffect(() => {
    let cancelled = false;

    async function init() {
      try {
        // 테스트 플래그: localStorage에 '__TEST_TOSS_SDK_FAIL__' 설정 시 SDK 초기화 실패 시뮬레이션
        if (localStorage.getItem('__TEST_TOSS_SDK_FAIL__')) {
          throw new Error('TEST_SDK_INIT_FAIL');
        }
        const timeoutPromise = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('SDK_INIT_TIMEOUT')), INIT_TIMEOUT_MS),
        );
        const widgets = (await Promise.race([
          createPaymentWidgets(customerKey as string),
          timeoutPromise,
        ])) as WidgetsInstance;
        if (cancelled) return;

        await widgets.setAmount({ currency: 'KRW', value: amount });
        if (cancelled) return;

        widgetsRef.current = widgets;
        setIsReady(true);
      } catch (err) {
        if (!cancelled) setInitError(err as Error);
      }
    }

    init();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerKey]);

  // 결제 UI 렌더링
  const renderPaymentMethods = useCallback(
    async (selector: string, variantKey = 'DEFAULT') => {
      if (!widgetsRef.current) return;
      const widget = await widgetsRef.current.renderPaymentMethods({
        selector,
        variantKey,
      });
      paymentMethodWidgetRef.current = widget;
      return widget;
    },
    [],
  );

  // 결제 요청 (Redirect)
  const requestPayment = useCallback(async (params: TossPaymentRequestParams) => {
    if (!widgetsRef.current) return;
    await widgetsRef.current.requestPayment(params);
  }, []);

  // 금액 변경
  const updateAmount = useCallback(async (newAmount: number) => {
    if (!widgetsRef.current) return;
    await widgetsRef.current.setAmount({ currency: 'KRW', value: newAmount });
  }, []);

  // 언마운트 시 위젯 destroy
  useEffect(() => {
    return () => {
      paymentMethodWidgetRef.current?.destroy();
      widgetsRef.current = null;
    };
  }, []);

  return {
    isReady,
    initError,
    renderPaymentMethods,
    requestPayment,
    updateAmount,
  };
}
